'use client'

import { Row, Stack } from '@christiankaindl/lyts'
import { useSearch, useSearchResults } from './SearchProvider'
import Button from '../Button/Button'
import { documentTypeFilters, documentTypeMappings, facetMappings, lawTypeMappings, sortOptionMapppings } from '@/lib/search'
import { ArrowDownNarrowWideIcon, CalendarFoldIcon, ChevronLeftIcon, ChevronRightIcon, SearchIcon, XIcon } from 'lucide-react'
import { useSearchOverlay } from '@/app/[category]/[subCategory]/[norm]/SearchOverlay'
import { Menu, MenuButton, MenuItem, MenuList } from '@/components/Menu'
import { theme } from '@/styles/theme.css'
import type { SearchResponseFacetCountSchema } from 'typesense/lib/Typesense/Documents'
import type { IDocument } from '@/lib/search.server'
import { Combobox, ComboboxItem, ComboboxList, ComboboxProvider } from '@ariakit/react'
import { Fragment, memo, startTransition, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { matchSorter } from 'match-sorter'
import { Input } from './Search'
import { formatDate, formatNumber } from '@/lib/utils'
import InputRow from '../Input/InputRow/InputRow'
import { LayoutGroup, motion, useMotionValueEvent, useScroll } from 'framer-motion'
import { searchFiltersScrollRoot } from './Search.css'
import useEvent from '@/hooks/useEvent'

const SearchFilters = memo(function SearchFilters ({ search, excludeFacets }: {
  /** Whether or not to show a search button that triggers the search overlay */
  search?: boolean
  /** Facets that should not be displayed */
  excludeFacets?: string[]
}) {
  const ref = useRef<HTMLDivElement>(null)

  return (
    <LayoutGroup>
      <div style={{ position: 'relative' }}>
        <Row
          gap={0.75}
          className={searchFiltersScrollRoot}
          ref={ref}
          style={{
            // @ts-expect-error CSS custom var
            '--reel-mask-right': 0,
            '--reel-mask-left': 0,
          }}
        >
          {!excludeFacets?.includes('documentType') && (
            <DocumentTypeFacet />
          )}
          <SortingMenu />
          <FacetOptions excludeFacets={excludeFacets} />
          {search && (
            <SearchButton />
          )}
        </Row>
        <Arrows ref={ref} />
      </div>
    </LayoutGroup>
  )
})

const transition = { type: 'spring', bounce: 0.1, visualDuration: 0.3 }
function Arrows ({ ref }: { ref: React.RefObject<HTMLDivElement | null> }) {
  const results = useSearchResults()

  const scroll = useScroll({ container: ref, axis: 'x' })
  const [arrows, setArrows] = useState<'both' | 'left' | 'right' | 'none'>('none')

  const updateArrows = useEvent(function updateArrows (value: number) {
    if (!ref.current) return

    if (value > 8 && value < (ref.current.scrollWidth - ref.current.clientWidth - 8)) {
      setArrows('both')
    } else if (value > 8) {
      setArrows('left')
    } else if (value < (ref.current.scrollWidth - ref.current.clientWidth - 8)) {
      setArrows('right')
    } else {
      setArrows('none')
    }
  })

  useLayoutEffect(() => {
    if (!ref.current) return
    ref.current.style.setProperty('--reel-mask-right', arrows === 'both' || arrows === 'right' ? null : '0')
    ref.current.style.setProperty('--reel-mask-left', arrows === 'both' || arrows === 'left' ? null : '0')
  }, [arrows, ref])

  useMotionValueEvent(scroll.scrollX, 'change', (value) => {
    updateArrows(value)
  })

  useEffect(() => {
    updateArrows(scroll.scrollX.get())
  }, [ref, results, scroll.scrollX, updateArrows])

  function next () {
    ref.current?.scrollBy({ left: 300, behavior: 'smooth' })
  }
  function prev () {
    ref.current?.scrollBy({ left: -300, behavior: 'smooth' })
  }

  if (!results) return null

  const showLeftArrow = arrows === 'both' || arrows === 'left'
  const showRightArrow = arrows === 'both' || arrows === 'right'
  return (
    <>
      <motion.div style={{ position: 'absolute', left: -9, zIndex: 10, top: '50%', y: '-50%' }} transition={transition} initial={{ scale: 0 }} animate={showLeftArrow ? { scale: 1 } : undefined}>
        <Button onClick={() => { prev() }} circular square size='small' style={{ backdropFilter: 'blur(6px)' }}>
          <ChevronLeftIcon size={16} />
        </Button>
      </motion.div>
      <motion.div style={{ position: 'absolute', right: -9, zIndex: 10, top: '50%', y: '-50%' }} transition={transition} initial={{ scale: 0 }} animate={showRightArrow ? { scale: 1 } : undefined}>
        <Button onClick={() => { next() }} circular square size='small' style={{ backdropFilter: 'blur(6px)' }}>
          <ChevronRightIcon size={16} />
        </Button>
      </motion.div>
    </>
  )
}

export default SearchFilters

function DocumentTypeFacet () {
  const results = useSearchResults()
  const { setFilter, filter, setSortBy } = useSearch()

  const isFilterActive = filter?.documentType !== undefined
  const facets = results?.facet_counts

  function resetFacets () {
    setFilter({ ...filter, documentType: undefined })
    setSortBy(undefined)
  }

  if (!facets || facets.length === 0) return null

  return (
    <Row gap={0.75}>
      {isFilterActive && (
        <Button size='small' circular square onClick={resetFacets}>
          <XIcon size={16} />
        </Button>
      )}
      <Row gap={0} style={{ backgroundColor: theme.colors.backgrounds.bgA2, borderRadius: 99 }}>
        {facets?.find((facet) => facet.field_name === 'documentType')?.counts.map((facet) => {
          if (facet.value === 'law') return null

          const isActive = filter?.documentType === facet.value

          if (isFilterActive && !isActive) return null

          function handleClick () {
            if (isActive) {
              resetFacets()
            } else {
              setFilter({
                ...filter,
                // @ts-expect-error needs runtime check
                documentType: facet.value,
              })
            }
          }
          return (
            <motion.span key={facet.value} layout='position'>
              <Button onClick={handleClick} variant={isActive ? 'brand' : 'flat'} circular size='small' style={{ boxShadow: 'none', display: 'flex' }}>
                {documentTypeMappings[facet.value] ?? facet.value}<span style={{ color: isActive ? 'white' : theme.colors.text.tertiary }}>{formatNumber(facet.count < 1000 ? facet.count : Math.round(facet.count / 10) * 10)}</span>
              </Button>
            </motion.span>
          )
        })}
      </Row>
    </Row>
  )
}

function SearchButton () {
  const dialog = useSearchOverlay((state) => state.dialog)

  return (
    <Button onClick={() => { dialog?.show() }} style={{ marginLeft: 'auto' }} variant='flat' circular title='Durchsuchen'>
      <SearchIcon size={16} />
    </Button>
  )
}

function DateRange ({ name }: { name: 'inEffectDate' | 'outOfEffectDate' | 'decisionDate' }) {
  const { filter, setFilter, pending } = useSearch()

  const dateFrom = pending?.filter?.[name]?.from ?? filter?.[name]?.from
  const dateTo = pending?.filter?.[name]?.to ?? filter?.[name]?.to

  return (
    <Menu>
      <MenuButton size='small' variant='raised' circular>
        <CalendarFoldIcon size={16} />
        {dateFrom && dateTo ? `${formatDate(dateFrom)} - ${formatDate(dateTo)}` : null}
        {dateFrom && !dateTo ? `Ab ${formatDate(dateFrom)}` : null}
        {!dateFrom && dateTo ? `Bis ${formatDate(dateTo)}` : null}
        {!dateFrom && !dateTo ? sortOptionMapppings[name].name : null}
      </MenuButton>
      <MenuList>
        <Stack gap={'6px'}>
          <InputRow type='date' label='Von' onChange={(event) => { if (greaterThan1900(event.target.value)) setFilter({ ...filter, [name]: { ...filter?.[name], from: event.target.value } }) }} value={dateFrom} />
          <InputRow type='date' label='Bis' onChange={(event) => { if (greaterThan1900(event.target.value)) setFilter({ ...filter, [name]: { ...filter?.[name], to: event.target.value } }) }} value={dateTo} />
        </Stack>
      </MenuList>
    </Menu>
  )
}

const TIMESTAMP_1900 = new Date('1900-01-01').getTime()
function greaterThan1900 (date: string) {
  return new Date(date).getTime() > TIMESTAMP_1900
}

function SortingMenu () {
  const results = useSearchResults()
  const { sortBy, setSortBy } = useSearch()

  const activeFilterOptions = Array.from(new Set(results?.facet_counts?.find((facet) => facet.field_name === 'documentType')?.counts.map((facet) => documentTypeFilters[facet.value].sortOptions).flat()))

  if (activeFilterOptions.length === 0) return null

  return (
    <Menu>
      <motion.div layout='position'>
        <MenuButton size='small' variant='raised' circular>
          <ArrowDownNarrowWideIcon size={16} /> {sortBy ? sortOptionMapppings[sortBy[0]].name : 'Sortieren'}
        </MenuButton>
      </motion.div>
      <MenuList>
        {activeFilterOptions?.map((option) => {
          return (
            <MenuItem key={option} onClick={() => { setSortBy([option]) }}>
              {sortOptionMapppings[option].name}
            </MenuItem>
          )
        })}
      </MenuList>
    </Menu>
  )
}

function FacetOptions ({ excludeFacets = [] }: { excludeFacets?: string[] }) {
  const results = useSearchResults()
  const facets = results?.facet_counts

  const documentTypeFacet = facets?.find((facet) => facet.field_name === 'documentType')
  const filtersToShow = Array.from(
    new Set(documentTypeFacet?.counts.map((facet) => documentTypeFilters[facet.value].facets).flat()),
  )

  return (
    <>
      {filtersToShow?.map((filter) => {
        if (filter.endsWith('Date')) {
          return (
            <motion.div key={filter} layout='position'>
              <DateRange name={filter} />
            </motion.div>
          )
        }

        const facet = facets?.find((facet) => facet.field_name === filter)

        if (!facet) return null
        if (facet.counts.length === 0) return null
        if (excludeFacets.includes(facet.field_name)) return null

        return (
          <motion.div key={filter} layout='position'>
            <FacetCombobox facet={facet} />
          </motion.div>
        )
      })}
    </>
  )
}

function FacetCombobox ({ facet }: { facet: SearchResponseFacetCountSchema<IDocument> }) {
  const [searchValue, setSearchValue] = useState('')

  const filter = useSearch((state) => state.filter)
  const setFilter = useSearch((state) => state.setFilter)

  // TODO: Group-by Landesrecht/Bundesrecht/EU-Recht
  const list = facet.counts.map((facetOption) => ({
    facetValue: facetOption.value,
    name: lawTypeMappings[facetOption.value.toLowerCase()] ?? facetOption.value,
  }))
  const FacetIcon = facetMappings[facet.field_name].icon
  const facetHasActiveFilter = filter?.[facet.field_name]

  const matches = useMemo(() => {
    return matchSorter(list, searchValue, {
      baseSort: (a, b) => (a.index < b.index ? -1 : 1),
      keys: ['name'],
    })
  }, [searchValue, list])

  return (
    <ComboboxProvider
      resetValueOnHide
      setValue={(value) => {
        startTransition(() => {
          setSearchValue(value)
        })
      }}
    >
      <Menu>
        <MenuButton size='small' variant={facetHasActiveFilter ? 'brand' : 'raised'} circular>
          {facetHasActiveFilter
            ? <XIcon size={16} onClick={() => { setFilter({ ...filter, [facet.field_name]: undefined }) }} />
            : <FacetIcon size={16} width={16} />
          }
          {lawTypeMappings[facetHasActiveFilter?.toLowerCase()] ?? facetHasActiveFilter ?? facetMappings[facet.field_name].name}
        </MenuButton>
        <MenuList>
          <Combobox
            render={<Input />}
            autoSelect
            placeholder="Filtern..."
            className="combobox"
          />
          <ComboboxList render={<Fragment />}>
            {matches.map((value) => (
              <ComboboxItem
                render={<MenuItem key={value.facetValue} onClick={() => { setFilter({ ...filter, [facet.field_name]: value.facetValue }) }} />}
                key={value.facetValue}
                value={value.name}
                focusOnHover
                setValueOnClick={false}
                className="menu-item"
              />
            ))}
          </ComboboxList>
        </MenuList>
      </Menu>
    </ComboboxProvider>
  )
}
